import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import _ from 'lodash-es';
import { Dealer } from '../../../shared/dealer/models/dealer';
import { Limit } from '../../asset-management/models/Limit';
import {
  ActionType,
  DispositionType,
  SpecLine,
  SpecificationType,
  getActionType,
  getDispositionType
} from '../../asset-management/models/SpecLine';
import { Specification } from '../../asset-management/models/Specification';
import { ROLES } from '../../authentication/authRoles.constants';
import { ActionDetails } from '../../asset-management/models/SpecLine';

export function generateSpecLineFromFormGroup(jsonFormValue: any) {
  const limits: Limit[] = [];
  limits.push(fillLimit(jsonFormValue.limit0));

  if (jsonFormValue.secondRepairLimitAdded) {
    limits.push(fillLimit(jsonFormValue.limit1));
  }

  const specLine: SpecLine = {
    id: '',
    legacyIds: [] as number[],
    v4SpecificationId: '',
    type: jsonFormValue.type,
    specCode: '',
    specDescription: '',
    specType: null,
    defaultSpec: jsonFormValue.isDefault,
    michelinSpec: false,
    exceedMichelinSpec: false,
    applicationDefinition: jsonFormValue.definition.applicationDefinition,
    sizes: jsonFormValue.definition.sizes,
    makes: jsonFormValue.definition.makes,
    axles: jsonFormValue.definition.axles,
    series: jsonFormValue.definition.series,
    treads: jsonFormValue.definition.treads,
    ntr: mapActionDetails(jsonFormValue.ntr),
    age: mapActionDetails(jsonFormValue.age),
    repair: mapActionDetails(jsonFormValue.limit0),
    limits
  };

  return specLine;
}

function mapActionDetails(formActionDetails: any): ActionDetails {
  const isActionDowngrade = getActionType(formActionDetails.action) === getActionType(ActionType.DOWNGRADE);
  const isDispositionReturn = getDispositionType(formActionDetails.disposition) === getDispositionType(DispositionType.RETURN);
  return {
    limit: formActionDetails.limit,
    action: formActionDetails.action,
    disposition: isActionDowngrade ? null : formActionDetails.disposition,
    disposalCode: isActionDowngrade || isDispositionReturn ? null : formActionDetails.disposalCode,
    application: !isActionDowngrade ? null : formActionDetails.application,
    axle: !isActionDowngrade ? null : formActionDetails.axle,
    treads: !isActionDowngrade ? [] : formActionDetails.treads
  };
}

export function fillLimit(formLimit: any): Limit {
  return {
    sectionNumber: formLimit.sectionNumber,
    totalRepairLimits: {
      totalRepairs: getNullSafeValue(formLimit.totalRepairs),
      nailRepairs: getNullSafeValue(formLimit.totalNail),
      rnailCrownRepairs: getNullSafeValue(formLimit.totalRnailCrown),
      rnailShoulder: {
        repairs: getNullSafeValue(formLimit.totalRnailShoulder),
        shoulder: {
          count: getNullSafeValue(formLimit.totalRnailShoulder),
          size: formLimit.rNailShoulderSize
        }
      },
      section: {
        repairs: getNullSafeValue(formLimit.totalSection),
        crown: {
          count: getNullSafeValue(formLimit.totalCrownSection),
          size: formLimit.maxCrownSize
        },
        shoulder: {
          count: getNullSafeValue(formLimit.totalShoulderSection),
          size: formLimit.maxShoulderSize
        },
        sidewall: {
          count: getNullSafeValue(formLimit.totalSidewallSection),
          size: formLimit.maxSidewallSize
        }
      }
    },
    otherRepairLimits: {
      spot: getNullSafeValue(formLimit.spot),
      bead: getNullSafeValue(formLimit.bead),
      liner: getNullSafeValue(formLimit.liner),
      brake: getNullSafeValue(formLimit.brakeSkid)
    }
  };
}

function getNullSafeValue(v: number) {
  return v;
}

export function logFormControlAndValidity(control: AbstractControl, path = ''): void {
  if (control instanceof FormControl) {
    if (!control.valid) {
      console.log(`${path} - ${control.valid} - ${control.value}`);
    }
  } else if (control instanceof FormGroup) {
    Object.keys(control.controls).forEach((controlName) => logFormControlAndValidity(control.get(controlName), `${path}.${controlName}`));
  } else if (control instanceof FormArray) {
    control.controls.forEach((controlName, index) => logFormControlAndValidity(controlName, `${path}.${index}`));
  }
}

export function populateLimitForm(limitFormGroup: AbstractControl, limit: Limit): void {
  limitFormGroup.patchValue({
    totalRepairs: limit.totalRepairLimits.totalRepairs,
    totalNail: limit.totalRepairLimits.nailRepairs,
    totalRnailCrown: limit.totalRepairLimits.rnailCrownRepairs,
    totalRnailShoulder: limit.totalRepairLimits.rnailShoulder.shoulder.count,
    rNailShoulderSize: limit.totalRepairLimits.rnailShoulder.shoulder.size,
    totalSection: limit.totalRepairLimits.section.repairs,
    totalCrownSection: limit.totalRepairLimits.section.crown.count,
    maxCrownSize: limit.totalRepairLimits.section.crown.size,
    totalShoulderSection: limit.totalRepairLimits.section.shoulder.count,
    maxShoulderSize: limit.totalRepairLimits.section.shoulder.size,
    totalSidewallSection: limit.totalRepairLimits.section.sidewall.count,
    maxSidewallSize: limit.totalRepairLimits.section.sidewall.size,
    spot: limit.otherRepairLimits.spot,
    bead: limit.otherRepairLimits.bead,
    liner: limit.otherRepairLimits.liner,
    brakeSkid: limit.otherRepairLimits.brake
  });
}

export function prepareSpecificationLineLabels(specification: Specification): void {
  const labelsMap = new Map<string, number>();

  specification.specificationLines.forEach((specLine) => {
    if (specLine.label === null) {
      if (!specLine.defaultSpec) {
        const key =
          specLine.type === SpecificationType.REPAIR_RETREAD ? specLine.treads.join(', ') : `${specLine.applicationDefinition} RO`;
        if (labelsMap.has(key)) {
          const newCount = labelsMap.get(key) + 1;
          labelsMap.set(key, newCount);
          specLine.label = `${key} (${newCount})`;
        } else {
          labelsMap.set(key, 0);
          specLine.label = `${key}`;
        }
      } else {
        specLine.label = specLine.type === SpecificationType.REPAIR_RETREAD ? 'Default RR' : 'Default RO';
      }
    }
  });

  const nonDefaultLines = specification.specificationLines.filter((line) => !line.defaultSpec);
  const defaultLines = specification.specificationLines.filter((line) => line.defaultSpec);

  const sortedNonDefaultLines = _.sortBy(nonDefaultLines, ['type', 'label']);
  const sortedDefaultLines = _.sortBy(defaultLines, ['type']);

  specification.specificationLines = [...sortedNonDefaultLines, ...sortedDefaultLines];
}

export function canEditCamSpec(isMichelinSpec: boolean, isNafaSpec: boolean, dealer: Dealer): boolean {
  const roles: string = localStorage.getItem('roles');
  return roles.includes(ROLES.edit_cams) && !isMichelinSpec && (!isNafaSpec || dealer.name === 'NAFA');
}

export function specCodeValidator(type: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const specCode = control.value;
    if (specCode && specCode.toLowerCase().startsWith('bt') && type.toLocaleLowerCase() === 'local') {
      return { invalidSpecCode: true };
    }
    return null;
  };
}
